/**
 * useSunoIntegration — manages Suno API state, cookie/credits, history sync,
 * Suite postMessage bridge, and direct generation trigger.
 * Extracted from App.tsx to reduce god component complexity.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getSunoCredits, getSunoFeed, triggerSunoGeneration, pollClipsByIds, getVideoStatus, upsampleTags } from '@services/sunoApi';
import { parseMarkdownToSunoOutput } from '@utils/markdownParser';
import { useToast } from '@components/ui';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { useUserSettings } from '@hooks/useUserSettings';
import type { ParsedSunoOutput, SunoClip } from '@/types';
import { resolveSunoGenerationModel } from '@/shared/sunoModels';
import type { SunoPrepPayload } from '@components/SunoPrep/SunoPrepModal';

export type SunoAuthStatus = 'unknown' | 'ok' | 'expired' | 'no-cookie';

/** Parse JWT bearer token and return UNIX ms of expiry, or null if not a JWT */
function parseJwtExpiryMs(token: string): number | null {
  try {
    if (!token || !token.startsWith('eyJ')) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    const payload = JSON.parse(json);
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export interface SunoBilling {
  credits: number;
  monthly_usage: number;
  monthly_limit: number;
  plan_name: string;
  plan_key?: string;
  features?: string[];
  is_active?: boolean;
  renews_on?: string;
  period?: string;
}

export interface GenerationResult {
  jobId: string;
  clips: SunoClip[];
  status: 'polling' | 'streaming' | 'complete' | 'error';
}

interface UseSunoIntegrationOptions {
  onSettingsOpen: () => void;
}

export function useSunoIntegration({ onSettingsOpen }: UseSunoIntegrationOptions) {
  const [sunoCookie, setSunoCookie] = useState('');
  const [sunoBilling, setSunoBilling] = useState<SunoBilling | null>(null);
  const [sunoAuthStatus, setSunoAuthStatus] = useState<SunoAuthStatus>('unknown');
  const [sunoTokenMinutesRemaining, setSunoTokenMinutesRemaining] = useState<number | null>(null);
  const [history, setHistory] = useState<SunoClip[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // ─── Suno Prep Modal State ──────────────────────────────
  const [sunoPrepOpen, setSunoPrepOpen] = useState(false);
  const [sunoPrepPayload, setSunoPrepPayload] = useState<Partial<SunoPrepPayload> | undefined>();
  const [sunoPrepGenerating, setSunoPrepGenerating] = useState(false);
  const pendingReplyRef = useRef<((data: unknown, error?: string) => void) | null>(null);

  // ─── Active Generation Tracking ────────────────────────
  const [activeGeneration, setActiveGeneration] = useState<GenerationResult | null>(null);
  const cancelPollingRef = useRef<(() => void) | null>(null);
  const pendingDraftIdRef = useRef<string | null>(null);

  const { addToast } = useToast();
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    addToast({ message, variant: type });
  }, [addToast]);

  const { save: saveUserSettings } = useUserSettings();

  // ─── Load cookie + history from localStorage ──────────────

  useEffect(() => {
    const cookie = localStorage.getItem('suno_cookie');
    if (cookie) setSunoCookie(cookie);
    const storedHistory = localStorage.getItem('suno_history');
    if (storedHistory) {
      try {
        setHistory(JSON.parse(storedHistory));
      } catch (e) {
        console.error('Failed to parse history from localStorage', e);
      }
    }
  }, []);

  // ─── Auto-capture poller ──────────────────────────────────
  // Polls the server every 60s for a token pushed by suno-session-capture.mjs.
  // When capturedAt is newer than the last seen value, silently loads and saves
  // the token so the user never has to touch the Admin Panel.
  const lastCapturedAtRef = useRef<string | null>(null);

  useEffect(() => {
    async function checkForNewToken() {
      try {
        const statusRes = await fetchWithCsrf('/api/suno/stored-token');
        if (!statusRes.ok) return;
        const status = await statusRes.json() as {
          hasToken: boolean;
          capturedAt?: string;
          isExpired?: boolean;
        };

        if (!status.hasToken || status.isExpired || !status.capturedAt) return;
        // Skip if we've already loaded this capture
        if (status.capturedAt === lastCapturedAtRef.current) return;

        const tokenRes = await fetchWithCsrf('/api/suno/load-stored-token');
        if (!tokenRes.ok) return;
        const { token } = await tokenRes.json() as { token: string };
        if (!token || !token.startsWith('eyJ')) return;

        // Already have this exact token — no need to re-save
        if (token === localStorage.getItem('suno_cookie')) {
          lastCapturedAtRef.current = status.capturedAt;
          return;
        }

        lastCapturedAtRef.current = status.capturedAt;
        localStorage.setItem('suno_cookie', token);
        setSunoCookie(token);
        await saveUserSettings({ suno_cookie: token });
        console.log('🔑 Suno token auto-refreshed from capture script');
        showToast('🎵 Suno token auto-refreshed');
      } catch {
        // Network / auth errors are non-fatal — just skip this tick
      }
    }

    // Check immediately on mount, then every 60s
    checkForNewToken();
    const id = setInterval(checkForNewToken, 60_000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Credits ──────────────────────────────────────────────

  const updateSunoBilling = useCallback(async (cookie: string) => {
    if (!cookie) { setSunoAuthStatus('no-cookie'); return; }
    try {
      const data = await getSunoCredits(cookie);
      setSunoBilling({
        credits: data.total_credits_left ?? data.credits ?? 0,
        monthly_usage: data.monthly_usage ?? 0,
        monthly_limit: data.monthly_limit ?? 0,
        plan_name: data.plan?.name ?? 'Unknown',
        plan_key: data.plan?.plan_key,
        features: data.plan?.usage_plan_features?.map((f: { name: string }) => f.name) ?? [],
        is_active: data.is_active,
        renews_on: data.renews_on,
        period: data.period,
      });
      setSunoAuthStatus('ok');
    } catch (err) {
      console.error('Failed to fetch Suno billing', err);
      setSunoBilling(null);
      const msg = err instanceof Error ? err.message : '';
      // getSunoCredits throws "Auth Failed (401)" / "(403)" / "(422)" on token rejection
      if (/\((401|403|422)\)/.test(msg)) {
        setSunoAuthStatus('expired');
      } else {
        setSunoAuthStatus('unknown');
      }
    }
  }, []);

  useEffect(() => {
    if (sunoCookie) {
      updateSunoBilling(sunoCookie);
    } else {
      setSunoBilling(null);
      setSunoAuthStatus('no-cookie');
    }
  }, [sunoCookie, updateSunoBilling]);

  // ─── JWT expiry countdown (tick every 30s) ────────────────
  useEffect(() => {
    if (!sunoCookie) { setSunoTokenMinutesRemaining(null); return; }
    const expMs = parseJwtExpiryMs(sunoCookie);
    if (!expMs) { setSunoTokenMinutesRemaining(null); return; }
    const tick = () => {
      const remaining = Math.floor((expMs - Date.now()) / 60000);
      setSunoTokenMinutesRemaining(remaining);
      if (remaining <= 0) setSunoAuthStatus('expired');
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [sunoCookie]);

  // ─── Suite PostMessage Bridge ─────────────────────────────

  useEffect(() => {
    const ALLOWED_TYPES = ['suite:open-settings', 'suite:suno-generate', 'suite:suno-feed', 'suite:suno-credits'];

    const handler = async (event: MessageEvent) => {
      const { type, id, payload } = event.data || {};
      if (!type || !id || !ALLOWED_TYPES.includes(type)) return;
      if (event.origin !== window.location.origin) return;

      const reply = (data: unknown, error?: string) => {
        const source = event.source as Window | null;
        if (source) {
          source.postMessage({ type: `${type}-result`, id, data, error }, event.origin);
        }
      };

      try {
        switch (type) {
          case 'suite:open-settings':
            onSettingsOpen();
            reply(null);
            break;

          case 'suite:suno-generate': {
            const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
            if (!cookie) { reply(null, 'Suno token not configured. Open HookHouse Settings to add it.'); return; }
            // Open Suno Prep Modal for review/edit instead of sending directly
            pendingReplyRef.current = reply;
            setSunoPrepPayload({
              title: payload.title || '',
              tags: payload.tags || '',
              negativeTags: payload.negativeTags || '',
              lyrics: payload.prompt || '',
              styleStrength: payload.styleStrength ?? 85,
              weirdness: payload.weirdness ?? 65,
              makeInstrumental: payload.makeInstrumental ?? false,
            });
            setSunoPrepOpen(true);
            break;
          }

          case 'suite:suno-feed': {
            const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
            if (!cookie) { reply(null, 'Suno token not configured. Open HookHouse Settings to add it.'); return; }
            const clips = await getSunoFeed(cookie);
            reply(clips);
            break;
          }

          case 'suite:suno-credits': {
            const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
            if (!cookie) { reply(null, 'Suno token not configured. Open HookHouse Settings to add it.'); return; }
            const credits = await getSunoCredits(cookie);
            reply(credits);
            break;
          }
        }
      } catch (err: unknown) {
        console.error(`[Suite Bridge] Error handling ${type}:`, err);
        reply(null, err instanceof Error ? err.message : 'Unknown error');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sunoCookie, onSettingsOpen]);

  // ─── Settings ─────────────────────────────────────────────

  const handleSaveSettings = useCallback(({ sunoCookie: cookie, sunoProxyUrl }: { sunoCookie: string; sunoProxyUrl: string }) => {
    setSunoCookie(cookie);
    localStorage.setItem('suno_cookie', cookie);
    localStorage.setItem('suno_proxy_url', sunoProxyUrl);
    showToast('Settings saved successfully.');
  }, [showToast]);

  // ─── History Sync ─────────────────────────────────────────

  const fetchAndMergeSunoHistory = useCallback(async () => {
    if (!sunoCookie) {
      showToast('Suno token not set. Please add it in settings.', 'error');
      return;
    }
    setIsSyncing(true);
    try {
      const allClips = await getSunoFeed(sunoCookie);
      const remoteClips = allClips.filter(c => c.is_public === true);
      // Only prune clips we have POSITIVE confirmation are now private
      // (is_public === false, present on this page of the feed). Never treat
      // mere absence from this single page as proof of unpublished — getSunoFeed
      // only fetches page 0, so an older public song can simply be off-page.
      const explicitlyPrivateIds = new Set(
        allClips.filter(c => c.is_public === false).map(c => c.id)
      );
      let prunedCount = 0;
      setHistory(prev => {
        let current = prev.filter(lc => {
          if (explicitlyPrivateIds.has(lc.id)) { prunedCount++; return false; }
          return true;
        });
        const localIds = new Set(current.map(c => c.id));
        const newClipIds: string[] = [];
        remoteClips.forEach(rc => {
          if (!localIds.has(rc.id)) {
            current.push(rc);
            newClipIds.push(rc.id);
          } else {
            current = current.map(lc => lc.id === rc.id ? { ...rc, originalData: lc.originalData } : lc);
          }
        });
        current.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        localStorage.setItem('suno_history', JSON.stringify(current));

        // Background-persist new clips to DB (fire-and-forget)
        for (const clip of remoteClips) {
          if (!clip.id || clip.id.startsWith('upload_')) continue;
          fetchWithCsrf('/api/suno-clip-history', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sunoClipId: clip.id,
              title: clip.title || null,
              modelName: clip.model_name || null,
              tags: clip.metadata?.tags || null,
              prompt: clip.metadata?.prompt || null,
              clipDataJson: JSON.stringify(clip),
            }),
          }).then(r => {
            // fetch resolves on HTTP errors -- without this check a 500 was invisible
            // (every save 500'd from 2026-03-15 to 2026-09-24, AUTO-043).
            if (!r.ok) console.warn(`[suno-clip-history] Background save rejected: HTTP ${r.status}`);
          }).catch((err: unknown) => {
            console.warn('[suno-clip-history] Background save failed (non-fatal):', err);
          });
        }

        return current;
      });

      // Prune the same confirmed-private clips from the DB mirror
      if (explicitlyPrivateIds.size > 0) {
        fetchWithCsrf('/api/suno-clip-history/bulk/by-clip-id', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sunoClipIds: Array.from(explicitlyPrivateIds) }),
        }).catch((err: unknown) => {
          console.warn('[suno-clip-history] Bulk prune failed (non-fatal):', err);
        });
      }

      const skipped = allClips.length - remoteClips.length;
      const parts: string[] = [];
      parts.push(`Synced ${remoteClips.length} published song${remoteClips.length === 1 ? '' : 's'}`);
      if (prunedCount > 0) parts.push(`removed ${prunedCount} now-private song${prunedCount === 1 ? '' : 's'}`);
      showToast(skipped > 0 || prunedCount > 0 ? `${parts.join(', ')}.` : 'Suno history synchronized.');
    } catch (err: unknown) {
      console.error('Sync Error:', err);
      showToast(err instanceof Error ? err.message : 'Failed to sync Suno history.', 'error');
    } finally {
      setIsSyncing(false);
    }
  }, [sunoCookie, showToast]);

  // ─── Direct Sync (send to Suno) ──────────────────────────

  const handleDirectSync = useCallback(async (dataToSync?: ParsedSunoOutput) => {
    if (!dataToSync || !sunoCookie) {
      showToast('Missing architecture output or Suno token. Check Settings.', 'error');
      return;
    }
    setIsSyncing(true);
    try {
      const model = resolveSunoGenerationModel(localStorage.getItem('suno_model'));
      await triggerSunoGeneration(dataToSync, sunoCookie, model, {
        styleStrength: dataToSync.styleStrength,
        weirdness: dataToSync.weirdness,
      });
      showToast(`Sync successful! ${dataToSync.title} has been sent to Suno.`, 'success');
      setTimeout(() => {
        fetchAndMergeSunoHistory();
        updateSunoBilling(sunoCookie);
      }, 2000);
    } catch (err: unknown) {
      console.error('Direct Sync Error:', err);
      const message = err instanceof Error ? err.message : 'Failed to trigger Suno generation.';
      // Suno's invisible captcha/session-trust check doesn't announce
      // itself in the error text -- the only known fix is pasting the
      // console script on suno.com/create and clicking Create once for
      // real, which silently re-validates the session. Since we can't
      // reliably detect *this specific* cause from the response body,
      // surface the real error plus that known remedy on every failure
      // rather than guessing via keyword match.
      showToast(
        `${message} — if this keeps happening, paste the Suno console script (Copy button) on suno.com/create and click Create once to refresh your session, then retry.`,
        'error'
      );
      // Re-throw so callers (Album Mode's per-track Sync button and the
      // batch Sync All runner) can tell a submission actually failed --
      // swallowing it here meant a rejected/422'd Suno request still
      // resolved successfully, so Album Mode marked tracks "Synced" even
      // when Suno never generated them.
      throw err;
    } finally {
      setIsSyncing(false);
    }
  }, [sunoCookie, showToast, fetchAndMergeSunoHistory, updateSunoBilling]);

  // ─── Suno Prep Modal Handlers ────────────────────────────

  const handleSunoPrepGenerate = useCallback(async (prep: SunoPrepPayload) => {
    const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
    if (!cookie) {
      showToast('Suno token not configured. Open HookHouse Settings to add it.', 'error');
      return;
    }
    pendingDraftIdRef.current = prep.draftId || null;
    setSunoPrepGenerating(true);
    try {
      // The model picked in the modal, else the saved setting; a retired model falls back to v6.
      const model = resolveSunoGenerationModel(prep.model, localStorage.getItem('suno_model'));
      const sunoPayload: ParsedSunoOutput = {
        titleArtist: prep.title || 'Suite Generation',
        summary: '', styleBlueprint: '',
        style: prep.tags || '', excludedStyle: prep.negativeTags || '',
        title: prep.title || 'Suite Generation', artist: '',
        lyricsWithTags: prep.lyrics || '',
        lyricsAlone: prep.makeInstrumental ? '' : (prep.lyrics || ''),
        javascriptCode: '', fullResponse: '',
        styleStrength: prep.styleStrength,
        weirdness: prep.weirdness,
      };
      const result = await triggerSunoGeneration(sunoPayload, cookie, model, {
        styleStrength: prep.styleStrength,
        weirdness: prep.weirdness,
        vocalGender: prep.vocalGender,
        maxMode: prep.maxMode,
        variety: prep.variety,
        durationSec: prep.durationSec,
        personalize: prep.personalize,
      });
      // Reply to the waiting suite app if this came from postMessage
      if (pendingReplyRef.current) {
        pendingReplyRef.current(result);
        pendingReplyRef.current = null;
      }
      showToast(`Sent to Suno: ${prep.title || 'Untitled'}`, 'success');
      // Tag the song with the model it was just sent to. Add-only and best effort: a tagging problem must
      // never undo or hide a generation that already went through.
      if (prep.draftId && prep.modelPill) {
        fetchWithCsrf(`/api/drafts/${prep.draftId}/suno-models/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model: prep.modelPill }),
        })
          .then(() => window.dispatchEvent(new CustomEvent('hookhouse:vault-updated', { detail: { draftId: prep.draftId } })))
          .catch((err: unknown) => console.warn('[suno] could not tag the song with its model:', err));
      }
      setSunoPrepOpen(false);

      // Extract clip IDs from generation response and start polling
      const clips: SunoClip[] = (result.clips || []).map((c: Record<string, unknown>) => ({
        id: c.id as string,
        title: (c.title as string) || prep.title || 'Untitled',
        created_at: (c.created_at as string) || new Date().toISOString(),
        model_name: (c.model_name as string) || model,
        status: c.status as string || 'submitted',
        audio_url: c.audio_url as string || undefined,
        video_url: c.video_url as string || undefined,
        batch_index: c.batch_index as number | undefined,
        metadata: { tags: null, prompt: null },
      }));
      const clipIds = clips.map(c => c.id).filter(Boolean);

      if (clipIds.length > 0) {
        setActiveGeneration({ jobId: result.id || clipIds[0], clips, status: 'polling' });

        // Cancel any existing polling
        if (cancelPollingRef.current) cancelPollingRef.current();

        cancelPollingRef.current = pollClipsByIds(cookie, clipIds, (updatedClips) => {
          if (updatedClips.length === 0) {
            // Timeout
            setActiveGeneration(prev => prev ? { ...prev, status: 'error' } : null);
            return;
          }

          const anyStreaming = updatedClips.some(c => c.status === 'streaming');
          const allComplete = updatedClips.every(c => c.status === 'complete' || c.status === 'error');
          const hasError = updatedClips.some(c => c.status === 'error');

          let status: GenerationResult['status'] = 'polling';
          if (allComplete) status = hasError ? 'error' : 'complete';
          else if (anyStreaming) status = 'streaming';

          setActiveGeneration(prev => ({
            jobId: prev?.jobId || clipIds[0],
            clips: updatedClips,
            status,
          }));

          if (allComplete) {
            const draftId = pendingDraftIdRef.current;
            if (draftId) {
              const completedIds = new Set(updatedClips.map(c => c.id));
              // Persist to DB
              for (const clip of updatedClips) {
                fetchWithCsrf('/api/suno-clip-history', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sunoClipId: clip.id,
                    title: clip.title || null,
                    modelName: clip.model_name || null,
                    tags: clip.metadata?.tags || null,
                    clipDataJson: JSON.stringify(clip),
                    draftId,
                  }),
                }).then(r => {
                  if (!r.ok) console.warn(`[suno-clip-history] Save after generation rejected: HTTP ${r.status}`);
                }).catch((err: unknown) => {
                  console.warn('[suno-clip-history] Save after generation failed (non-fatal):', err);
                });
              }
              // Also stamp draftId onto history state so it shows in the UI
              setHistory(prev => {
                const next = prev.map(c => completedIds.has(c.id) ? { ...c, draftId } : c);
                localStorage.setItem('suno_history', JSON.stringify(next));
                return next;
              });
            }
            fetchAndMergeSunoHistory();
            updateSunoBilling(cookie);
          }
        });
      } else {
        // Fallback: no clip IDs in response, just refresh after delay
        setTimeout(() => {
          fetchAndMergeSunoHistory();
          updateSunoBilling(cookie);
        }, 2000);
      }
    } catch (err: unknown) {
      console.error('Suno Prep generation error:', err);
      const msg = err instanceof Error ? err.message : 'Failed to trigger Suno generation.';
      if (pendingReplyRef.current) {
        pendingReplyRef.current(null, msg);
        pendingReplyRef.current = null;
      }
      showToast(msg, 'error');
    } finally {
      setSunoPrepGenerating(false);
    }
  }, [sunoCookie, showToast, fetchAndMergeSunoHistory, updateSunoBilling]);

  const handleSunoPrepClose = useCallback(() => {
    setSunoPrepOpen(false);
    // Reply with cancellation so suite app's promise resolves
    if (pendingReplyRef.current) {
      pendingReplyRef.current(null, 'Cancelled by user');
      pendingReplyRef.current = null;
    }
  }, []);

  // ─── Video Status Polling (after audio completes) ──────
  const videoPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref so the video polling closure always calls the latest showToast without
  // needing it in the effect's dependency array (avoids restart on every render)
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => {
    // Start video polling when generation completes
    if (activeGeneration?.status !== 'complete') {
      if (videoPollingRef.current) {
        clearInterval(videoPollingRef.current);
        videoPollingRef.current = null;
      }
      return;
    }

    const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
    if (!cookie) return;

    const clipIds = activeGeneration.clips.map(c => c.id);
    let attempts = 0;

    const pollVideo = async () => {
      if (attempts >= 30) {
        if (videoPollingRef.current) clearInterval(videoPollingRef.current);
        videoPollingRef.current = null;
        showToastRef.current(
          'Video generation timed out after 5 minutes. Audio is ready — video may still appear later on Suno.',
          'error'
        );
        return;
      }
      attempts++;

      try {
        const updates = await Promise.all(
          clipIds.map(async (id) => {
            try {
              return await getVideoStatus(cookie, id);
            } catch {
              return null;
            }
          })
        );

        setActiveGeneration(prev => {
          if (!prev) return null;
          const updatedClips = prev.clips.map((clip, i) => {
            const videoData = updates[i];
            if (!videoData) return clip;
            return {
              ...clip,
              video_status: videoData.status,
              video_url: videoData.video_url || clip.video_url,
            };
          });
          return { ...prev, clips: updatedClips };
        });

        const allVideosDone = updates.every(u => u?.status === 'complete');
        if (allVideosDone && videoPollingRef.current) {
          clearInterval(videoPollingRef.current);
          videoPollingRef.current = null;
        }
      } catch (err) {
        console.error('[video poll] Error:', err);
      }
    };

    pollVideo();
    videoPollingRef.current = setInterval(pollVideo, 10000);

    return () => {
      if (videoPollingRef.current) {
        clearInterval(videoPollingRef.current);
        videoPollingRef.current = null;
      }
    };
  }, [activeGeneration?.status, sunoCookie]);

  /** Cancel mid-flight generation — stops polling and marks as error, but keeps widget visible. */
  const cancelGeneration = useCallback(() => {
    if (cancelPollingRef.current) {
      cancelPollingRef.current();
      cancelPollingRef.current = null;
    }
    if (videoPollingRef.current) {
      clearInterval(videoPollingRef.current);
      videoPollingRef.current = null;
    }
    setActiveGeneration(prev => prev ? { ...prev, status: 'error' } : null);
    showToast('Generation cancelled.', 'error');
  }, [showToast]);

  /** Dismiss the generation widget entirely (after complete/error). */
  const dismissGeneration = useCallback(() => {
    setActiveGeneration(null);
    if (cancelPollingRef.current) {
      cancelPollingRef.current();
      cancelPollingRef.current = null;
    }
    if (videoPollingRef.current) {
      clearInterval(videoPollingRef.current);
      videoPollingRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (cancelPollingRef.current) cancelPollingRef.current();
    };
  }, []);

  // ─── Tag Upsample ──────────────────────────────────────
  const handleTagUpsample = useCallback(async (tags: string): Promise<string | null> => {
    const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
    if (!cookie) {
      showToast('Suno token not configured.', 'error');
      return null;
    }
    try {
      const result = await upsampleTags(cookie, tags);
      return result.tags || null;
    } catch (err) {
      console.error('Tag upsample error:', err);
      showToast('Upsample unavailable — feature may require Premier plan or endpoint may have changed.', 'error');
      return null;
    }
  }, [sunoCookie, showToast]);

  const openSunoPrep = useCallback((payload?: Partial<SunoPrepPayload>) => {
    pendingReplyRef.current = null; // No postMessage reply for manual opens
    setSunoPrepPayload(payload || {});
    setSunoPrepOpen(true);
    // Refresh billing when modal opens
    const cookie = localStorage.getItem('suno_cookie') || sunoCookie;
    if (cookie) updateSunoBilling(cookie);
  }, [sunoCookie, updateSunoBilling]);

  // ─── Upload History ───────────────────────────────────────

  const handleUploadHistory = useCallback(async (files: File[]) => {
    let successCount = 0;
    let failCount = 0;
    const newClips: SunoClip[] = [];

    for (const file of files) {
      try {
        const text = await file.text();
        const parsed = parseMarkdownToSunoOutput(text);
        const clip: SunoClip = {
          id: `upload_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          title: parsed.title,
          created_at: new Date().toISOString(),
          model_name: 'Uploaded Blueprint',
          metadata: { tags: parsed.style, prompt: parsed.lyricsWithTags },
          originalData: parsed,
        };
        newClips.push(clip);
        successCount++;
      } catch (err: unknown) {
        console.error(`Failed to parse ${file.name}:`, err instanceof Error ? err.message : err);
        failCount++;
      }
    }

    if (newClips.length > 0) {
      setHistory(prev => {
        const updated = [...newClips, ...prev];
        localStorage.setItem('suno_history', JSON.stringify(updated));
        return updated;
      });
    }

    if (failCount === 0) {
      showToast(`Successfully uploaded ${successCount} blueprint(s).`, 'success');
    } else if (successCount > 0) {
      showToast(`Uploaded ${successCount} blueprints. ${failCount} failed to parse.`, 'error');
    } else {
      showToast('Upload failed. No valid blueprints found in files.', 'error');
    }
  }, [showToast]);

  // Backward-compatible derived credit value
  const sunoCredits = sunoBilling?.credits ?? null;

  return {
    sunoCookie, setSunoCookie,
    sunoCredits,
    sunoBilling,
    sunoAuthStatus,
    sunoTokenMinutesRemaining,
    history, setHistory,
    isSyncing,
    showToast,
    handleSaveSettings,
    updateSunoBilling,
    fetchAndMergeSunoHistory,
    handleDirectSync,
    handleUploadHistory,
    // Suno Prep Modal
    sunoPrepOpen,
    sunoPrepPayload,
    sunoPrepGenerating,
    handleSunoPrepGenerate,
    handleSunoPrepClose,
    openSunoPrep,
    // Active generation tracking
    activeGeneration,
    cancelGeneration,
    dismissGeneration,
    // Tag upsample
    handleTagUpsample,
  };
}
